import { ArrowRight, ArrowUpRight, Menu } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useFetcher } from 'react-router';

import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Languages, type LanguageT } from '~/localization/i18n';

const NAV_LINKS = [
  { to: '/about', key: 'nav.about' },
  { to: '/services', key: 'nav.services' },
  { to: '/locations', key: 'nav.locations' },
  { to: '/resources', key: 'nav.resources' },
  { to: '/careers', key: 'nav.careers' },
  { to: '/faq', key: 'nav.faq' },
  { to: '/contact', key: 'nav.contact' },
] as const;

// The intake questionnaire lives in the bhfield-public app.
export const GET_STARTED_URL = 'https://public.bhfield.com/preliminary-assessment-questionnaire';

// BH Field Academy is a separate property — always opened in a new tab.
export const ACADEMY_URL = 'https://bhfield.academy';

export function SiteNav() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 px-4 backdrop-blur-md backdrop-saturate-150 supports-[backdrop-filter]:bg-background/75 md:px-6 lg:px-8 xl:px-24">
      <nav className="mx-auto flex max-w-[1248px] items-center justify-between py-5">
        <LogoMark />
        {/* Desktop horizontal links — xl+ only: eight links, the toggle and the CTA do not fit one row below 1280px */}
        <ul className="hidden items-center gap-6 whitespace-nowrap text-[15px] text-foreground xl:flex 2xl:gap-9">
          {NAV_LINKS.map(({ to, key }) => (
            <li key={key}>
              <Link
                to={to}
                className="relative inline-block rounded-sm transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 after:pointer-events-none after:absolute after:-bottom-1.5 after:left-1/2 after:h-0.5 after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full"
              >
                {t(key)}
              </Link>
            </li>
          ))}
          <li>
            <a
              href={ACADEMY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-sm text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t('nav.academy')}
              <ArrowUpRight aria-hidden className="size-3.5 text-muted-foreground" strokeWidth={2} />
            </a>
          </li>
        </ul>
        <div className="flex shrink-0 items-center gap-3">
          <LanguageToggle />
          <Link
            to="/get-started"
            className="hidden items-center gap-2 whitespace-nowrap rounded-md bg-primary px-6 py-3 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:inline-flex"
          >
            {t('nav.getStarted')} <ArrowRight className="size-4" strokeWidth={2} />
          </Link>
          {/* Mobile hamburger — last in the right cluster, hidden on xl+ */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label={t('nav.openMenu')}
                className="inline-flex size-11 items-center justify-center rounded-md border border-border bg-card text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 xl:hidden"
              >
                <Menu aria-hidden className="size-5" strokeWidth={1.75} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[min(320px,85vw)] p-6">
              <SheetTitle className="text-base font-medium">{t('nav.menu')}</SheetTitle>
              <ul className="mt-6 flex flex-col gap-1">
                {NAV_LINKS.map(({ to, key }) => (
                  <li key={key}>
                    <Link
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className="flex min-h-11 items-center rounded-md px-3 py-2 text-[15px] text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      {t(key)}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href={ACADEMY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-[15px] text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {t('nav.academy')}
                    <ArrowUpRight aria-hidden className="size-3.5 text-muted-foreground" strokeWidth={2} />
                  </a>
                </li>
              </ul>
              <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
                <Link
                  to="/get-started"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {t('nav.getStarted')} <ArrowRight className="size-4" strokeWidth={2} />
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}

/**
 * EN/ES switch. Posts to the root action, which stores the language in the
 * session; loaders revalidate and the whole tree re-renders in the new locale.
 */
function LanguageToggle() {
  const { i18n } = useTranslation();
  const fetcher = useFetcher();
  const current = (i18n.language as LanguageT) === Languages.ES ? Languages.ES : Languages.EN;
  const next = current === Languages.EN ? Languages.ES : Languages.EN;

  return (
    <fetcher.Form method="post" action="/">
      <input type="hidden" name="_action" value="onChangeLanguage" />
      <input type="hidden" name="language" value={next} />
      <button
        type="submit"
        aria-label={next === Languages.ES ? 'Cambiar a español' : 'Switch to English'}
        className="inline-flex min-h-11 items-center justify-center rounded-md border border-border bg-card px-3 font-mono text-[13px] tracking-[0.52px] text-foreground uppercase transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {next}
      </button>
    </fetcher.Form>
  );
}

function LogoMark() {
  const { t } = useTranslation();
  return (
    <Link
      to="/"
      aria-label={t('nav.homeAria')}
      className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <img
        src="/bhfield-logo.svg"
        alt=""
        width={142}
        height={60}
        className="block h-[40px] w-auto md:h-[48px] lg:h-[52px]"
      />
    </Link>
  );
}
